import React from 'react';
import { useCanvasStageOperations } from 'state/canvas/stage';
import { isMobile } from 'react-device-detect';
import { isUndefined } from 'lodash';
import { Position } from 'types';

interface Props {
  start: Position;
  removeArrow: Function;
  surface: string;
}

export function useLinkArrowState({ removeArrow, start, surface }: Props) {
  const { getCanvasStage } = useCanvasStageOperations();
  const endWithOffsetRef = React.useRef<any>();

  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseDown);
    document.addEventListener('keydown', onEscPress);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseDown);
      document.removeEventListener('keydown', onEscPress);
    };
  }, []);

  const [{ end, isDrawing }, setState] = React.useState({
    isDrawing: true,
    end: { x: undefined, y: undefined },
  });

  const offset = 20;

  const direction = captureMouseDirection(start, end);

  endWithOffsetRef.current = getEndWithOffset(end, offset);

  if (isMobile && isUndefined(end.x) && isUndefined(end.y)) {
    // Offset should be divided by stage scale
    endWithOffsetRef.current.x = start.x - 50;
    endWithOffsetRef.current.y = start.y - 200;
  }

  function onEscPress(event) {
    const { key } = event;
    if (key === 'Escape') {
      removeArrow();
    }
  }

  function handleMouseDown() {
    if (isDrawing) {
      if (isMobile) {
        if (
          endWithOffsetRef.current.x === start.x - 50 &&
          endWithOffsetRef.current.y === start.y - 200
        ) {
          return;
        }
      }

      setTimeout(removeArrow, 500);
      setState((state) => ({ ...state, isDrawing: false }));
    }
  }

  function captureMouseDirection(start, end) {
    let directionX = '',
      directionY = '';

    if (end.x < start?.x) {
      directionX = 'left';
    } else if (end.x > start?.x) {
      directionX = 'right';
    }

    if (end.y < start?.y) {
      directionY = 'top';
    } else if (end.y > start?.y) {
      directionY = 'bottom';
    }

    return directionY + '-' + directionX;
  }

  function handleMouseMove(event) {
    const x = event.targetTouches
      ? event.targetTouches[0].clientX
      : event.clientX;

    const y = event.targetTouches
      ? event.targetTouches[0].clientY
      : event.clientY;

    const stage = getCanvasStage();

    const end = {
      x: x / stage.scale - stage.x / stage.scale + offset,
      y: y / stage.scale - stage.y / stage.scale + offset,
    };

    // @ts-ignore
    setState((state) => ({ ...state, end, isDrawing: true }));
  }

  function getEndWithOffset(end, offset) {
    const endWithOffset = { x: end.x, y: end.y };

    if (direction.includes('left')) {
      endWithOffset.x = (end.x + offset) as number;
    }

    if (direction.includes('right')) {
      endWithOffset.x = (end.x - offset * 2) as number;
    }

    if (direction.includes('top')) {
      endWithOffset.y = (end.y + offset) as number;
    }

    if (direction.includes('bottom')) {
      endWithOffset.y = (end.y - offset) as number;
    }

    return endWithOffset;
  }

  return { end: endWithOffsetRef.current, start };
}
